import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Target,
  Phone,
  Mic,
  Mail,
  Users,
  Sparkles,
  CalendarDays,
  FolderKanban,
  BarChart3,
  Search,
  CheckCircle2,
  ArrowRight,
  Zap,
  Shield,
  Star,
  Play,
  TrendingUp,
  MessageSquare,
  Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  {
    icon: Search,
    title: "Find Businesses",
    description:
      "Search Google Places by category, city, and state. Import thousands of qualified leads in seconds.",
  },
  {
    icon: Target,
    title: "Lead Pipeline",
    description:
      "Track every prospect from first touch to closed-won. Drag-and-drop pipeline, custom statuses, full history.",
  },
  {
    icon: Phone,
    title: "Click-to-Call + Recording",
    description:
      "Twilio-powered two-legged dialing with dual-channel recording. Managers can coach reps by listening back.",
  },
  {
    icon: Mic,
    title: "Call Recording Library",
    description:
      "Every call is auto-recorded and catalogued. Role-based playback — owners, managers, and the rep who called.",
  },
  {
    icon: Mail,
    title: "Email Outreach",
    description:
      "Send from your verified domain via Resend, SendGrid, or SMTP. Templates, tracking, and reply detection.",
  },
  {
    icon: Sparkles,
    title: "AI Sales Intelligence",
    description:
      "10-mode GPT-powered reports: call prep, objection handling, email drafts, lead scoring, industry briefs.",
  },
  {
    icon: CalendarDays,
    title: "Booking Calendar",
    description:
      "Calendly-style public booking links. Prospects self-schedule. Reps get email notifications automatically.",
  },
  {
    icon: FolderKanban,
    title: "Project Manager",
    description:
      "Organize deals and team work on drag-and-drop boards. Assign cards, track stages, attach files.",
  },
  {
    icon: BarChart3,
    title: "Analytics & Reports",
    description:
      "Live dashboards for call volume, email opens, conversion rates, and rep performance leaderboards.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Build your pipeline",
    body:
      "Search Google for businesses in your territory, import CSVs, or add leads by hand. Every record stays in sync.",
  },
  {
    step: "02",
    title: "Call, email, and book meetings",
    body:
      "Dial through Twilio, send from your verified domain, and let prospects book themselves via public calendar links.",
  },
  {
    step: "03",
    title: "Coach and close",
    body:
      "Listen to recorded calls, review activity feeds, and let AI draft your next email. Managers see everything.",
  },
];

function Navbar() {
  return (
    <nav className="sticky top-0 z-40 border-b border-slate-200/60 bg-white/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 min-w-0 flex-shrink-0" data-testid="landing-logo">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-sm sm:text-lg text-slate-900 whitespace-nowrap">
            Sales CRM Coach
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
          <a href="#features" className="hover:text-slate-900 transition">Features</a>
          <a href="#how" className="hover:text-slate-900 transition">How it works</a>
          <a href="#pricing" className="hover:text-slate-900 transition">Pricing</a>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Link to="/login">
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="nav-signin-btn">
              Sign In
            </Button>
            <Button variant="ghost" size="sm" className="sm:hidden px-2 text-xs" data-testid="nav-signin-btn-mobile">
              Sign In
            </Button>
          </Link>
          <Link to="/register">
            <Button size="sm" className="text-xs sm:text-sm px-3 sm:px-4" data-testid="nav-signup-btn">
              <span className="hidden sm:inline">Start Free Trial</span>
              <span className="sm:hidden">Start Trial</span>
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Soft gradient background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50 via-white to-slate-50" />
      <div className="absolute top-0 right-0 -z-10 w-[500px] h-[500px] bg-blue-200/30 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="animate-fade-in-up">
          <Badge variant="secondary" className="mb-4 sm:mb-6 gap-1 px-3 py-1.5">
            <Zap className="w-3.5 h-3.5" />
            Built for cold-calling sales teams
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            Close more deals.
            <br />
            <span className="text-blue-600">Faster.</span>
          </h1>
          <p className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            Sales CRM Coach is an all-in-one CRM that finds businesses, powers click-to-call dialing with recording, drafts your emails with AI, and coaches your team — all in one place.
          </p>
          <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="flex-1 sm:flex-none">
              <Button size="lg" className="w-full sm:w-auto text-sm sm:text-base h-11 sm:h-12 px-5 sm:px-6" data-testid="hero-cta-btn">
                Start your free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#features" className="flex-1 sm:flex-none">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-sm sm:text-base h-11 sm:h-12 px-5 sm:px-6">
                See features
              </Button>
            </a>
          </div>
          <div className="mt-5 sm:mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs sm:text-sm text-slate-500">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              7-day free trial
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              No credit card required
            </div>
          </div>
        </div>

        {/* Mock dashboard preview */}
        <div className="relative lg:h-[500px] mt-4 lg:mt-0">
          <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-blue-100/60 overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400" />
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400" />
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400" />
              <span className="ml-2 sm:ml-3 text-[10px] sm:text-xs text-slate-500 font-mono truncate">app.salescrmcoach.com/dashboard</span>
            </div>
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {[
                  { label: "Calls today", value: "47", icon: Phone, color: "blue" },
                  { label: "New leads", value: "128", icon: Target, color: "green" },
                  { label: "Meetings booked", value: "12", icon: CalendarDays, color: "purple" },
                ].map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="border border-slate-200 rounded-lg p-2 sm:p-3">
                      <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-${stat.color}-600 mb-1.5 sm:mb-2`} />
                      <div className="text-lg sm:text-2xl font-bold text-slate-900">{stat.value}</div>
                      <div className="text-[10px] sm:text-xs text-slate-500">{stat.label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="border border-slate-200 rounded-lg p-3 sm:p-4 space-y-2">
                <div className="flex items-center justify-between text-xs sm:text-sm gap-2">
                  <span className="font-medium text-slate-700 truncate">Acme Manufacturing</span>
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 flex-shrink-0 text-xs">Qualified</Badge>
                </div>
                <div className="text-[10px] sm:text-xs text-slate-500">San Francisco, CA • Last call 2h ago</div>
                <div className="flex gap-1.5 pt-1">
                  <div className="h-1.5 bg-blue-500 rounded flex-1" />
                  <div className="h-1.5 bg-blue-500 rounded flex-1" />
                  <div className="h-1.5 bg-blue-500 rounded flex-1" />
                  <div className="h-1.5 bg-slate-200 rounded flex-1" />
                  <div className="h-1.5 bg-slate-200 rounded flex-1" />
                </div>
              </div>
              <div className="border border-slate-200 rounded-lg p-2.5 sm:p-3 flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs sm:text-sm font-medium text-slate-700 truncate">AI draft ready</div>
                  <div className="text-[10px] sm:text-xs text-slate-500 truncate">Follow-up email for Acme</div>
                </div>
                <Button size="sm" variant="outline" className="flex-shrink-0 text-xs">Review</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="max-w-2xl">
          <Badge variant="secondary" className="mb-4">Features</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
            Everything your sales team needs, nothing they don&apos;t.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Built by a sales coach for sales teams. Replace 6 tools with one.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card
                key={feature.title}
                className="border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardHeader>
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-2">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="py-16 sm:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="secondary" className="mb-4">How it works</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
            From cold list to closed deal in 3 steps
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {HOW_IT_WORKS.map((step) => (
            <div key={step.step} className="relative">
              <div className="text-7xl font-heading font-bold text-blue-100 leading-none mb-2">
                {step.step}
              </div>
              <h3 className="text-xl font-heading font-bold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-slate-600 leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BillingExplainer() {
  return (
    <div className="mt-10 max-w-4xl mx-auto rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 sm:p-8" data-testid="billing-explainer">
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex flex-shrink-0 w-11 h-11 rounded-lg bg-blue-600 items-center justify-center">
          <Phone className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-bold text-slate-900">
            How telecom billing works
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Each plan includes a generous monthly call-minute allotment. Beyond that, pay only for what you use — automatically billed on your next invoice.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Included Minutes</div>
              <p className="mt-1 text-slate-700">500/mo (Pro) · 2,000/mo (Enterprise)</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Overage</div>
              <p className="mt-1 text-slate-700">$0.03/min (Pro) · $0.02/min (Enterprise)</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Phone Numbers</div>
              <p className="mt-1 text-slate-700">$3/number/month · pro-rated</p>
            </div>
            <div className="bg-white rounded-lg border border-green-200 p-3 ring-1 ring-green-100">
              <div className="text-xs font-semibold text-green-700 uppercase tracking-wide flex items-center gap-1">
                <Mic className="w-3 h-3" />
                Call Recording
              </div>
              <p className="mt-1 text-slate-700">
                <strong>Free</strong> · dual-channel · unlimited storage
              </p>
            </div>
          </div>

          {/* Dedicated recording pricing explainer */}
          <div className="mt-4 rounded-lg bg-white border border-slate-200 p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0">
                <Mic className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">How call recording pricing works</h4>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                  Every call is <strong>automatically recorded in dual-channel</strong> (rep + lead on separate tracks) at
                  no extra charge to you. We absorb Twilio&apos;s $0.0025/min recording fee and $0.0005/min storage fee
                  <em> so you can keep recordings forever</em>, review them for coaching, and let managers listen back.
                  Pricing is tied to <strong>call minutes only</strong> — there is no per-recording charge, no storage
                  cap, and no egress fee when you stream recordings in the app.
                </p>
              </div>
            </div>
          </div>
          <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              Recordings &amp; storage are <strong>free</strong> — keep them forever
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              80% &amp; 100% allotment alerts so there are no surprises
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              Calls keep working past 100% — sales doesn&apos;t stop for billing
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              One Stripe invoice — no separate Twilio bill
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/public/plans`)
      .then((r) => r.json())
      .then((d) => {
        setPlans(d.plans || []);
      })
      .catch(() => {
        setPlans([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="pricing" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="secondary" className="mb-4">Pricing</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
            Simple pricing that scales with your team
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Start with a free trial. Upgrade anytime. Cancel anytime.
          </p>
        </div>

        <BillingExplainer />

        {loading ? (
          <div className="mt-12 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : plans.length === 0 ? (
          <div className="mt-12 text-center text-slate-500">
            Pricing is being finalized. <Link to="/register" className="text-blue-600 underline">Join the free trial</Link> for now.
          </div>
        ) : (
          <div
            className={`mt-14 grid gap-6 ${
              plans.length === 1
                ? "max-w-md mx-auto"
                : plans.length === 2
                ? "md:grid-cols-2 max-w-4xl mx-auto"
                : "md:grid-cols-2 lg:grid-cols-3"
            }`}
            data-testid="pricing-plans"
          >
            {plans.map((plan) => (
              <Card
                key={plan.plan_id}
                className={`relative ${
                  plan.is_popular
                    ? "border-blue-500 border-2 shadow-xl shadow-blue-100"
                    : "border-slate-200"
                }`}
                data-testid={`plan-${plan.plan_id}`}
              >
                {plan.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white gap-1 px-3 py-1">
                      <Star className="w-3 h-3 fill-current" />
                      Most popular
                    </Badge>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  {plan.description && (
                    <p className="text-sm text-slate-500">{plan.description}</p>
                  )}
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-4xl font-heading font-bold text-slate-900">
                      ${Number(plan.price_per_seat).toFixed(0)}
                    </span>
                    <span className="text-slate-500">/user/month</span>
                  </div>
                  {plan.trial_days > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      {plan.trial_days}-day free trial
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/register" className="block">
                    <Button
                      className="w-full"
                      variant={plan.is_popular ? "default" : "outline"}
                      data-testid={`plan-cta-${plan.plan_id}`}
                    >
                      Start free trial
                    </Button>
                  </Link>
                  {plan.features?.length > 0 && (
                    <ul className="space-y-2 pt-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {plan.daily_leads != null && plan.daily_leads !== -1 && (
                    <p className="text-xs text-slate-500 pt-2">
                      Up to {plan.daily_leads} leads/day per user
                    </p>
                  )}
                  {plan.included_voice_minutes > 0 && (
                    <p className="text-xs text-slate-500">
                      {plan.included_voice_minutes.toLocaleString()} call minutes/mo included
                      {plan.overage_per_minute && ` · $${plan.overage_per_minute}/min overage`}
                    </p>
                  )}
                  {plan.phone_number_fee > 0 && (
                    <p className="text-xs text-slate-500">
                      Phone numbers: ${plan.phone_number_fee}/mo each
                    </p>
                  )}
                  <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Call recording + storage: <span className="font-semibold">Free</span>
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-16 sm:py-24 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <Shield className="w-12 h-12 text-blue-200 mx-auto mb-4" />
        <h2 className="font-heading text-3xl sm:text-4xl font-bold">
          Ready to give your team an unfair advantage?
        </h2>
        <p className="mt-4 text-lg text-blue-100">
          Try Sales CRM Coach free for 7 days. No credit card required.
        </p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to="/register">
            <Button size="lg" variant="secondary" className="text-base h-12 px-6" data-testid="final-cta-btn">
              Start your free trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/login">
            <Button
              size="lg"
              variant="outline"
              className="text-base h-12 px-6 bg-transparent border-white text-white hover:bg-white hover:text-blue-700"
            >
              Sign in
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 py-8 sm:py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center">
            <Target className="w-4 h-4 text-white" />
          </div>
          <span>© {new Date().getFullYear()} Sales CRM Coach. All rights reserved.</span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/login" className="hover:text-slate-900">Sign in</Link>
          <Link to="/register" className="hover:text-slate-900">Get started</Link>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />
      <Hero />
      <Features />
      <ProductShowcase />
      <HowItWorks />
      <Pricing />
      <FinalCTA />
      <Footer />
    </div>
  );
}

function ProductShowcase() {
  const [activeTab, setActiveTab] = useState("leads");
  const tabs = [
    { id: "leads", label: "Pipeline", icon: Target },
    { id: "calls", label: "Call Recording", icon: Mic },
    { id: "intel", label: "AI Sales Intel", icon: Sparkles },
    { id: "kanban", label: "Project Manager", icon: FolderKanban },
    { id: "calendar", label: "Booking", icon: CalendarDays },
  ];

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Badge variant="secondary" className="mb-4">Take a tour</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
            See Sales CRM Coach in action
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            A glimpse of what your team uses every day to close deals faster.
          </p>
        </div>

        {/* Tab navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="showcase-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`showcase-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Rendered screenshot */}
        <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-blue-100/60 overflow-hidden max-w-5xl mx-auto">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-400 flex-shrink-0" />
            <div className="w-3 h-3 rounded-full bg-yellow-400 flex-shrink-0" />
            <div className="w-3 h-3 rounded-full bg-green-400 flex-shrink-0" />
            <span className="ml-3 text-[10px] sm:text-xs text-slate-500 font-mono truncate">
              app.salescrmcoach.com/{activeTab === "leads" ? "leads" : activeTab === "calls" ? "calls" : activeTab === "intel" ? "sales-intelligence" : activeTab === "kanban" ? "projects" : "calendar"}
            </span>
          </div>
          <div className="bg-white overflow-x-auto">
            <div className="min-w-[640px] sm:min-w-0">
              {activeTab === "leads" && <MockPipeline />}
              {activeTab === "calls" && <MockCalls />}
              {activeTab === "intel" && <MockIntel />}
              {activeTab === "kanban" && <MockKanban />}
              {activeTab === "calendar" && <MockCalendar />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MockPipeline() {
  const rows = [
    { name: "Acme Manufacturing", city: "San Francisco, CA", status: "Qualified", color: "blue", rep: "JD" },
    { name: "BrightSky Consulting", city: "Austin, TX", status: "Contacted", color: "amber", rep: "MS" },
    { name: "Pinnacle Medical Group", city: "Chicago, IL", status: "Prospect", color: "slate", rep: "JD" },
    { name: "Harbor Law Firm", city: "Boston, MA", status: "Closed Won", color: "green", rep: "KL" },
    { name: "Summit Real Estate", city: "Denver, CO", status: "Qualified", color: "blue", rep: "MS" },
  ];
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-bold text-slate-900">Leads</h3>
          <p className="text-xs text-slate-500">2,847 total · 184 active</p>
        </div>
        <div className="flex gap-2">
          <div className="px-3 py-1.5 text-xs bg-slate-100 rounded-md text-slate-600">Filter</div>
          <div className="px-3 py-1.5 text-xs bg-blue-600 rounded-md text-white">+ New Lead</div>
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 border-b border-slate-200">
          <div className="col-span-4">Business</div>
          <div className="col-span-3">Location</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Rep</div>
          <div className="col-span-1 text-right">•••</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
            <div className="col-span-4 font-medium text-slate-800">{r.name}</div>
            <div className="col-span-3 text-slate-500">{r.city}</div>
            <div className="col-span-2">
              <span className={`text-xs px-2 py-0.5 rounded bg-${r.color}-100 text-${r.color}-700`}>{r.status}</span>
            </div>
            <div className="col-span-2">
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-medium text-slate-600">{r.rep}</div>
            </div>
            <div className="col-span-1 text-right text-slate-400">•••</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockCalls() {
  const calls = [
    { lead: "Acme Manufacturing", dur: "4:23", outcome: "Qualified", time: "2h ago", hasRec: true },
    { lead: "BrightSky Consulting", dur: "7:11", outcome: "Demo Scheduled", time: "3h ago", hasRec: true },
    { lead: "Pinnacle Medical", dur: "1:02", outcome: "No Answer", time: "5h ago", hasRec: false },
    { lead: "Harbor Law Firm", dur: "12:47", outcome: "Closed Won", time: "yesterday", hasRec: true },
  ];
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-bold text-slate-900">Call Recordings</h3>
          <p className="text-xs text-slate-500">Every call auto-recorded. Coach your reps.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Clock className="w-3.5 h-3.5" />
          Today: 47 calls · 3h 21m
        </div>
      </div>
      <div className="space-y-2">
        {calls.map((c, i) => (
          <div key={i} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-blue-200">
            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center">
              {c.hasRec ? <Play className="w-4 h-4 text-blue-600 fill-blue-600" /> : <Phone className="w-4 h-4 text-slate-400" />}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-800">{c.lead}</div>
              <div className="text-xs text-slate-500 flex gap-3">
                <span>{c.dur}</span>
                <span>•</span>
                <span>{c.time}</span>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded ${c.outcome === "Closed Won" ? "bg-green-100 text-green-700" : c.outcome === "No Answer" ? "bg-slate-100 text-slate-500" : "bg-blue-50 text-blue-700"}`}>
              {c.outcome}
            </span>
            {c.hasRec && (
              <div className="hidden md:flex items-center gap-0.5 h-6">
                {[8, 14, 6, 18, 12, 20, 10, 16, 8, 14].map((h, j) => (
                  <div key={j} className="w-0.5 bg-blue-400 rounded-full" style={{ height: `${h}px` }} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MockIntel() {
  return (
    <div className="p-6 grid md:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <h3 className="text-lg font-heading font-bold text-slate-900">Sales Intelligence</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">AI-powered briefing for your next call</p>
        <div className="space-y-3">
          {[
            "📊 Industry snapshot",
            "🎯 Call prep script",
            "💬 Objection handlers",
            "📧 First-touch email",
            "🧮 Deal scoring",
            "📝 Follow-up plan",
          ].map((t, i) => (
            <div key={i} className={`px-3 py-2 rounded-md border text-sm ${i === 0 ? "border-purple-400 bg-purple-50 text-purple-900 font-medium" : "border-slate-200 text-slate-600"}`}>
              {t}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 space-y-3">
        <div className="text-xs uppercase tracking-wide text-purple-600 font-semibold">Industry Snapshot</div>
        <p className="leading-relaxed">
          <strong>Acme Manufacturing</strong> operates in the <strong>industrial automation</strong> sector. The industry is experiencing 12% YoY growth driven by reshoring initiatives and labor shortages.
        </p>
        <div className="grid grid-cols-3 gap-2 pt-2">
          <div className="bg-white rounded p-2 border border-slate-200">
            <div className="text-xs text-slate-500">Employees</div>
            <div className="text-lg font-bold text-slate-900">~180</div>
          </div>
          <div className="bg-white rounded p-2 border border-slate-200">
            <div className="text-xs text-slate-500">Revenue</div>
            <div className="text-lg font-bold text-slate-900">$22M</div>
          </div>
          <div className="bg-white rounded p-2 border border-slate-200">
            <div className="text-xs text-slate-500">Score</div>
            <div className="text-lg font-bold text-green-600">87</div>
          </div>
        </div>
        <div className="pt-2 flex items-center gap-2 text-xs text-slate-500">
          <Zap className="w-3.5 h-3.5 text-amber-500" />
          Generated in 2.4s by GPT-5.2
        </div>
      </div>
    </div>
  );
}

function MockKanban() {
  const cols = [
    { name: "Backlog", cards: ["Launch Q2 campaign", "Hire SDR"], color: "slate" },
    { name: "In Progress", cards: ["Build demo deck", "Follow-up sequence"], color: "blue" },
    { name: "Review", cards: ["Enterprise proposal"], color: "amber" },
    { name: "Done", cards: ["Website redesign", "Email templates"], color: "green" },
  ];
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-lg font-heading font-bold text-slate-900">Q2 Sales Motion</h3>
          <p className="text-xs text-slate-500">Drag cards across columns · assign teammates · attach files</p>
        </div>
        <div className="flex items-center gap-1">
          {["JD", "MS", "KL"].map((i) => (
            <div key={i} className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[10px] font-medium text-slate-600">{i}</div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {cols.map((col) => (
          <div key={col.name} className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-slate-700">{col.name}</div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded bg-${col.color}-100 text-${col.color}-700`}>{col.cards.length}</span>
            </div>
            <div className="space-y-2">
              {col.cards.map((c, i) => (
                <div key={i} className="bg-white rounded-md p-2.5 border border-slate-200 shadow-sm text-xs text-slate-700">
                  <div className="font-medium mb-1.5">{c}</div>
                  <div className="flex items-center justify-between">
                    <div className="w-5 h-5 rounded-full bg-slate-200" />
                    <span className="text-[10px] text-slate-400">Due Apr 30</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockCalendar() {
  const slots = ["9:00 AM", "10:30 AM", "1:00 PM", "2:30 PM", "4:00 PM"];
  return (
    <div className="p-6 grid md:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CalendarDays className="w-4 h-4 text-blue-600" />
          <h3 className="text-lg font-heading font-bold text-slate-900">Book a meeting with Darryl</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">30-minute strategy call · salescrmcoach.com/book/darryl</p>
        <div className="grid grid-cols-7 gap-1.5 text-xs">
          {["S","M","T","W","T","F","S"].map((d) => (
            <div key={d} className="text-center text-[10px] text-slate-500 font-medium">{d}</div>
          ))}
          {Array.from({ length: 35 }).map((_, i) => {
            const day = i - 3;
            const isActive = day === 15;
            const disabled = day < 5 || day > 28;
            return (
              <div
                key={i}
                className={`aspect-square flex items-center justify-center rounded-md text-xs ${
                  isActive ? "bg-blue-600 text-white font-bold" : disabled ? "text-slate-300" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {day > 0 && day <= 30 ? day : ""}
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-blue-600 font-semibold mb-3">Tuesday, April 15</div>
        <div className="space-y-2">
          {slots.map((s, i) => (
            <div
              key={i}
              className={`px-4 py-3 rounded-md border text-sm transition-all ${
                i === 2 ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-slate-200 text-slate-700 hover:border-blue-300"
              }`}
            >
              {s}
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-green-50 rounded-md border border-green-200 text-xs text-green-800 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Instant confirmation · Google Calendar sync available
        </div>
      </div>
    </div>
  );
}
